/* Core & libraries */
import React, { useEffect, useState, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Field } from "formik";

/* Helpers, constants, custom methods, internal libraries */

/* Components */
import { ReactComponent as CloseIconSvg } from "../../resources/images/close-icon.svg";

/* Styles */
import styles from "./styles";
import { useTheme } from "react-jss";

const DELIM_REGEX = /[,\s]+/;            
const ENDS_WITH_DELIM = /[,\s]$/;       

const MultiValueField = ({
  values = [],
  setFieldTouched,
  onValuesChange,
  placeholder,
  disabled = false,
  maxValues = null,
  errors = {},
  name = "",
  ariaDescribedby
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const classes = styles({ theme });
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef(null);

  const isMaxReached = maxValues != null && values.length >= maxValues;

  const errorsOnField = errors?.[name] || null;
  const notConformItems = typeof errorsOnField === 'string'
    ? values.filter((v) => errorsOnField.includes(v))
    : [];

  useEffect(() => {
    if (isMaxReached) {
      document.getElementById("max-message")?.focus();
    }
  }, [isMaxReached]);

  useEffect(() => {
    if (errorsOnField) setFieldTouched?.(name, true);
  }, [errorsOnField, name, setFieldTouched]);

  const commitValues = (list) => {
    if (!onValuesChange) return;
    onValuesChange(list);
  };

  const parseAndAddValues = (raw) => {
    if (!raw) return;

    const tokens = raw
      .split(DELIM_REGEX)
      .map((t) => t.trim())
      .filter(Boolean);

    if (!tokens.length) return;

    const existing = new Set(values.map((v) => (v ?? "").toString().toLowerCase()));
    const seen = new Set();
    const next = [...values];

    for (const t of tokens) {
      const key = t.toLowerCase();
      if (!existing.has(key) && !seen.has(key)) {
        if (maxValues != null && next.length >= maxValues) break;
        next.push(t);
        seen.add(key);
      }
    }

    if (next.length !== values.length) commitValues(next);
    setInputValue("");
  };

  const handleRemoveValue = (indexToRemove) => {
    const next = values.filter((_, i) => i !== indexToRemove);
    commitValues(next);
  };

  const handleInputChange = (e) => {
    const val = e.target.value ?? "";
    // If user types/pastes a delimiter at the end, tokenize immediately
    if (ENDS_WITH_DELIM.test(val)) {
      parseAndAddValues(val);
    } else {
      setInputValue(val);
    }
  };

  const handleInputKeyDown = (e) => {
    const isEnter = e.key === "Enter";
    const isSpace = e.key === " " || e.key === "Spacebar";
    const isComma = e.key === ",";

    if (isEnter || isSpace || isComma) {
      e.preventDefault();
      parseAndAddValues(inputValue);
      return;
    }

    if (e.key === "Backspace" && !inputValue && values.length > 0) {
      handleRemoveValue(values.length - 1);
    }
  };

  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  const onBlurAdd = () => {
    setFieldTouched?.(name, true);
    if (inputValue) parseAndAddValues(inputValue);
  };

  return (
    <>
      <div
        className={`${classes.container} ${errorsOnField ? classes.errorContainer : ""}`}
        onClick={handleContainerClick}
        aria-label={placeholder}
      >
        <div className={classes.valuesContainer}>
          {values.map((value, index) => {
            const applyErrorStyling = notConformItems.includes(value);
            return (
              <div
                key={`${value}-${index}`}
                className={`${classes.valueTag} ${applyErrorStyling ? classes.errorBorder : ""}`}
              >
                <span
                  className={`${classes.valueText} ${applyErrorStyling ? classes.errorText : ""}`}
                >
                  {value}
                </span>
                <button
                  type="button"
                  className={classes.removeButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveValue(index);
                  }}
                  disabled={disabled}
                  data-testid={`remove-button-${value}`}
                >
                  <CloseIconSvg
                    className={`${classes.closeIcon} ${applyErrorStyling ? classes.errorIcon : ""}`}
                    aria-label={`${formatMessage({ id: "ucMultiValueField.remove" })} ${value}`}
                  />
                </button>
              </div>
            );
          })}

          {!isMaxReached && (
            <Field
              type="text"
              name={name}
              render={() => (
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  placeholder={values.length === 0 ? placeholder : ""}
                  className={classes.addInput}
                  disabled={disabled}
                  aria-label={placeholder}
                  aria-describedby={ariaDescribedby}
                  onBlur={onBlurAdd}
                />
              )}
            />
          )}
        </div>
      </div>

      {isMaxReached && (
        <div tabIndex={0} id="max-message" className={classes.maxMessage}>
          <FormattedMessage id="ucMultiValueField.maxValues" values={{ maxValues }} />
        </div>
      )}
    </>
  );
};

export default MultiValueField;
